import { MAX_VISIT_RECORDINGS } from "@rosc/contracts";
import type { TFunction } from "i18next";
import { StatusFile } from "@/common/enums/enums";

// Same statuses as isInProgress in visit-queries (kept here so this check has
// no dependency on the read layer).
const inProgress = (status: string | null | undefined): boolean =>
  status === StatusFile.PROCESSING || status === StatusFile.GENERATING;

// Why "Add recording" is unavailable for a visit, or null when it can be used.
// - busy: the visit or one of its recordings is still processing or
//   generating. A recording added now would make that job fail and leave the
//   visit in Error, so wait until it settles.
// - limit: the visit already holds the most recordings allowed.
export type AddRecordingBlock = "busy" | "limit" | null;

export const addRecordingBlock = (
  file: { status?: string | null } | null | undefined,
  recordings: readonly { status?: string | null }[] = [],
): AddRecordingBlock => {
  if (!file) return null;
  if (
    inProgress(file.status) ||
    recordings.some((recording) => inProgress(recording.status))
  )
    return "busy";
  if (1 + recordings.length >= MAX_VISIT_RECORDINGS) return "limit";
  return null;
};

export const addRecordingBlockMessage = (
  block: AddRecordingBlock,
  t: TFunction,
): string | null =>
  block === "busy"
    ? t("visit.sidebar.multiRecording.busy")
    : block === "limit"
      ? t("visit.sidebar.multiRecording.limit", {
          count: MAX_VISIT_RECORDINGS,
        })
      : null;

// Whether the visit's main note was changed after it was written (an edit,
// a confirmed or corrected value, an applied Ask edit): Add recording writes
// the note again from all the recordings, so those changes are not carried
// over. `original` is the note as first written; after an earlier Add
// recording it stays the first note, so a rebuilt note also counts as
// changed (the notice is still true then: the current note is replaced).
export const noteChangedSinceWritten = (
  file: { result?: string | null; original?: string | null } | null | undefined,
): boolean => {
  const result = file?.result?.trim();
  const original = file?.original?.trim();
  return !!result && !!original && result !== original;
};

// The version that holds the note Add recording replaced: the newest
// version that was not there when the recording was submitted.
export const replacedNoteVersion = (
  before: readonly string[],
  versions: readonly { public_id: string; version: number }[] = [],
): string | null => {
  const added = versions.filter(
    (version) => !before.includes(String(version.public_id)),
  );
  if (!added.length) return null;
  return String(
    added.reduce((a, b) => (b.version > a.version ? b : a)).public_id,
  );
};
